import { createQueryKeys, type Query } from '@shared/api';

/**
 * Ключи запросов учётных записей.
 *
 * Заведены ради подсказки работников (ADR 0102): она спрашивается из формы учётки и зависит сразу
 * от двух вещей — набранного текста и самой учётки, чьи приметы служат подсказкой, когда текста
 * ещё нет. Сырой массив в компоненте пришлось бы повторить и в чтении, и в сбросе после привязки,
 * а разошедшийся на один элемент ключ означал бы список кандидатов, который не обновился после
 * того, как одного из них уже привязали.
 *
 * Семейства сидят под общим корнем `['users']` намеренно: всякое действие над учёткой — заведение,
 * правка, архив, возврат, решение по заявке на регистрацию — гасит корень целиком, и всё, что
 * посчитано из тех же учёток, обязано уходить вместе с ним.
 */
export const userAccountKeys = createQueryKeys('users', {
  /**
   * Вкладка «Пользователи»: отбор целиком, как его собрал экран.
   *
   * Сегмента с именем семейства у списка нет — ключ так и остаётся `['users', отбор]`. Это не
   * забытая `list`: сегмент сменил бы ячейку кэша единственному экрану вкладки, ничего при этом не
   * починив, — корень `['users']` и сейчас накрывает список вместе со всеми выборками.
   *
   * Отбор объектом целиком, а не полями врозь: фильтров у вкладки девять — роль, запрошенная роль,
   * площадка, контрагент, активность, заявки на регистрацию, архив и окно дат, — и перечисленный
   * руками список однажды забыли бы дополнить: вкладка показала бы прежние строки под новым
   * фильтром.
   */
  list: (params: Query) => [params],
  /**
   * Сколько заявок на регистрацию ждут решения (ADR 0034) — цифра бейджа в меню и та же цифра над
   * вкладкой.
   *
   * Одно семейство на обоих спрашивающих, потому что вопрос у них дословно один: вторая ячейка
   * означала бы второй поход за тем же числом и бейдж, расходящийся со вкладкой, которую как раз
   * открыли по нему. Параметров у ключа нет — счётчик один на портал, отбором вкладки он не
   * сужается.
   *
   * Под корнем учёток, а не под своим: решение по заявке и есть правка учётки, и погасший корень
   * обязан унести число вместе со списком. Собственного корня хватило бы ровно до первой
   * активации — бейдж продолжал бы звать в очередь, которую уже разобрали.
   */
  pendingCount: () => ['pending-count'],
  /** The current account card shown at the end of its audit path. */
  detail: (id: string) => ['detail', id],
  /** The same complete user list serves audit filters, grant pickers and access overview. */
  options: () => ['options'],
  // Учётки может ещё не быть (карточку заводят вместе с ней), и это часть ключа: подсказка новой
  // учётке строится по набранному ФИО, а существующей — по её приметам, и путать два ответа нельзя.
  personCandidates: (params: { userId: string | null; query: string }) => [
    'person-candidates',
    params,
  ],
  /**
   * Кандидаты в руководители отдела — по одной роли на запрос.
   *
   * Роль входит в ключ, потому что она и есть запрос: у ручки учёток фильтр `role` принимает одно
   * значение, и карточка отдела спрашивает столько раз, сколько ролей работает на отдельской оси.
   * Общий ключ на все роли отдал бы всем страницам ответ той, которая спросила первой.
   */
  departmentHeadCandidates: (role: string) => ['department-head-candidates', role],
});
